import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

const MEAL_LABELS = { petit_dejeuner: 'Petit-déj', dejeuner: 'Déjeuner', gouter: 'Goûter', diner: 'Dîner' };
const CAT_LABELS = { ecole: 'École', activite: 'Activité', medical: 'Médical', anniversaire: 'Anniversaire', autre: 'Autre' };

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/dashboard/today').then(r => setData(r.data)).catch(() => {});
  }, []);

  const today = new Date();
  const dateStr = today.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const toggleHabit = async (habitId) => {
    await api.post(`/habits/${habitId}/toggle`, {});
    const r = await api.get('/dashboard/today');
    setData(r.data);
  };

  const toggleTask = async (task) => {
    await api.put(`/tasks/${task.id}`, { status: task.status === 'fait' ? 'a_faire' : 'fait' });
    const r = await api.get('/dashboard/today');
    setData(r.data);
  };

  if (!data) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-light)' }}>Chargement...</div>;

  const tasksUndone = data.tasks.filter(t => t.status !== 'fait').length;
  const tasksDone = data.tasks.filter(t => t.status === 'fait').length;
  const habitsTotal = data.habits.length;
  const habitsDone = data.habitLogs.length;
  const isHabitDone = (id) => data.habitLogs.some(l => l.habit_id === id);

  return (
    <div>
      <div className="dashboard-welcome">Bonjour, {user.name.split(' ')[0]} !</div>
      <div className="dashboard-date">{dateStr.charAt(0).toUpperCase() + dateStr.slice(1)}</div>

      <div className="dashboard-grid">
        <div className="stat-card">
          <div className="stat-emoji">&#9989;</div>
          <div className="stat-value" style={{ color: 'var(--success)' }}>{tasksDone}/{tasksDone + tasksUndone}</div>
          <div className="stat-label">Tâches faites</div>
        </div>
        <div className="stat-card">
          <div className="stat-emoji">&#127947;</div>
          <div className="stat-value" style={{ color: 'var(--orange)' }}>{data.sport.filter(s => s.completed).length}/{data.sport.length}</div>
          <div className="stat-label">Sport</div>
        </div>
        <div className="stat-card">
          <div className="stat-emoji">&#127775;</div>
          <div className="stat-value" style={{ color: 'var(--primary)' }}>{habitsDone}/{habitsTotal}</div>
          <div className="stat-label">Habitudes</div>
        </div>
        <div className="stat-card">
          <div className="stat-emoji">&#128722;</div>
          <div className="stat-value" style={{ color: 'var(--teal)' }}>{data.shopping.done}/{data.shopping.total}</div>
          <div className="stat-label">Courses</div>
        </div>
      </div>

      {/* Tâches du jour */}
      {data.tasks.length > 0 && (
        <div className="day-section">
          <div className="day-section-title"><span className="section-emoji">&#128203;</span> Tâches du jour</div>
          <div className="task-list">
            {data.tasks.map(task => {
              const showDossier = task.category === 'travail' && task.reference;
              const colorStyle = task.color ? { borderLeft: `5px solid ${task.color}`, paddingLeft: 11 } : {};
              return (
                <div key={task.id} className={`task-item ${task.status === 'fait' ? 'done' : ''}`} style={colorStyle}>
                  <div className={`task-checkbox ${task.status === 'fait' ? 'checked' : ''}`} onClick={() => toggleTask(task)}>
                    {task.status === 'fait' && <span>&#10003;</span>}
                  </div>
                  <div className="task-info">
                    {showDossier ? (
                      <>
                        <div className="task-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: '0.9rem' }}>&#128193;</span>
                          <span>{task.reference}</span>
                        </div>
                        <div style={{ fontSize: '0.82rem', color: 'var(--text-light)', fontWeight: 500, marginTop: 2 }}>
                          {task.title}
                        </div>
                      </>
                    ) : (
                      <div className="task-title">{task.title}</div>
                    )}
                    <div className="task-meta">
                      <span className={`badge badge-${task.priority}`}>{task.priority}</span>
                      <span className={`badge badge-${task.category}`}>{task.category}</span>
                      {task.subtheme && <span className="badge" style={{ background: '#f1f5f9', color: '#475569' }}>{task.subtheme}</span>}
                      {task.due_time && <span className="task-date">{task.due_time.slice(0, 5)}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Activités des enfants */}
      {data.childActivities.length > 0 && (
        <div className="day-section">
          <div className="day-section-title"><span className="section-emoji">&#128118;</span> Enfants aujourd'hui</div>
          {data.childActivities.map(act => (
            <div key={act.id} className="timeline-item">
              <div className="timeline-time">{act.start_time ? act.start_time.slice(0, 5) : '--:--'}</div>
              <div className="timeline-dot" style={{ background: act.child_color }} />
              <div className="timeline-content">
                <div className="timeline-title">{act.title}</div>
                <div className="timeline-sub">{act.child_name} &middot; <span className={`badge badge-${act.category}`}>{CAT_LABELS[act.category]}</span></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Repas du jour */}
      {data.meals.length > 0 && (
        <div className="day-section">
          <div className="day-section-title"><span className="section-emoji">&#127858;</span> Repas prévus</div>
          {data.meals.map(meal => (
            <div key={meal.id} className="timeline-item">
              <div className="timeline-time" style={{ fontSize: '0.72rem' }}>{MEAL_LABELS[meal.meal_type]}</div>
              <div className="timeline-dot" style={{ background: 'var(--success)' }} />
              <div className="timeline-content">
                <div className="timeline-title">{meal.title}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sport */}
      {data.sport.length > 0 && (
        <div className="day-section">
          <div className="day-section-title"><span className="section-emoji">&#127947;</span> Sport</div>
          {data.sport.map(s => (
            <div key={s.id} className="timeline-item">
              <div className="timeline-time">{s.start_time ? s.start_time.slice(0, 5) : '--:--'}</div>
              <div className="timeline-dot" style={{ background: s.completed ? 'var(--success)' : 'var(--warning)' }} />
              <div className="timeline-content">
                <div className="timeline-title">{s.activity}</div>
                <div className="timeline-sub">{s.duration_min ? s.duration_min + ' min' : ''} {s.intensity && '· ' + s.intensity}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Habitudes */}
      {data.habits.length > 0 && (
        <div className="day-section">
          <div className="day-section-title"><span className="section-emoji">&#127775;</span> Habitudes du jour</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {data.habits.map(h => (
              <button
                key={h.id}
                onClick={() => toggleHabit(h.id)}
                style={{
                  padding: '10px 18px', borderRadius: 12,
                  border: isHabitDone(h.id) ? 'none' : '2px solid var(--border)',
                  background: isHabitDone(h.id) ? h.color : 'white',
                  color: isHabitDone(h.id) ? 'white' : 'var(--text)',
                  fontWeight: 600, fontSize: '0.85rem', transition: 'all 0.2s',
                  cursor: 'pointer',
                }}
              >
                {isHabitDone(h.id) ? '✓ ' : ''}{h.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* État vide */}
      {data.tasks.length === 0 && data.childActivities.length === 0 && data.meals.length === 0 && data.sport.length === 0 && data.habits.length === 0 && (
        <div className="empty-state">
          <div className="empty-emoji">&#128640;</div>
          <p>Votre journée est vide ! Ajoutez des tâches, repas, séances de sport ou habitudes depuis les modules.</p>
        </div>
      )}
    </div>
  );
}
